import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  FilterList,
  Inventory2,
  LocalDining,
  Science,
  Search,
  Tune,
} from '@mui/icons-material';

const ingredients = [
  {
    name: 'Whey protein concentrate',
    category: 'Substance',
    description: 'High-protein dairy powder for nutritional formulations',
    tags: ['Substance', 'Milk allergen', 'Solid'],
    selected: true,
  },
  {
    name: 'Pea protein isolate',
    category: 'Substance',
    description: 'Protein isolate used in bars, drinks and powder formulations',
    tags: ['Substance', 'Protein', 'Solid'],
  },
  {
    name: 'Pectin',
    category: 'Substance',
    description: 'Gelling and texture ingredient for fruit and dairy products',
    tags: ['Substance', 'E-number', 'Technical effect'],
  },
  {
    name: 'Sunflower oil',
    category: 'Substance',
    description: 'Vegetable oil used for texture and fat content adjustment',
    tags: ['Substance', 'Liquid', 'Fat source'],
  },
];

const filters = [
  'Type: Substance',
  'Technical effect',
  'Regulatory: E-number',
  'External identifiers',
];

const previewValues = [
  { label: 'Protein', value: '80 g' },
  { label: 'Physical state', value: 'Solid' },
];

function Tag({ label, tone = 'default' }) {
  const isWarning = tone === 'warning';

  return (
    <Box
      sx={{
        px: 0.65,
        py: 0.28,
        borderRadius: 999,
        fontSize: 9.4,
        fontWeight: 900,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: isWarning ? 'warning.dark' : 'primary.main',
        bgcolor: (theme) =>
          isWarning
            ? alpha(theme.palette.warning.main, 0.13)
            : alpha(theme.palette.primary.main, 0.08),
        border: '1px solid',
        borderColor: (theme) =>
          isWarning
            ? alpha(theme.palette.warning.main, 0.28)
            : alpha(theme.palette.primary.main, 0.18),
      }}
    >
      {label}
    </Box>
  );
}

function IngredientRow({ ingredient }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.9,
        borderRadius: 2,
        bgcolor: ingredient.selected
          ? (theme) => alpha(theme.palette.primary.main, 0.035)
          : 'background.paper',
        borderColor: ingredient.selected
          ? (theme) => alpha(theme.palette.primary.main, 0.22)
          : 'divider',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Inventory2 sx={{ fontSize: 15 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 900,
              fontSize: 10.8,
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {ingredient.name}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontWeight: 700,
              fontSize: 9.7,
              lineHeight: 1.25,
              mt: 0.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {ingredient.description}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0 }}>
          {ingredient.tags.slice(0, 2).map((tag) => (
            <Tag
              key={tag}
              label={tag}
              tone={tag.includes('allergen') ? 'warning' : 'default'}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function IngredientSearchVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 300,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Search sx={{ fontSize: 16 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Ingredient search
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Search by name, type, category, annotation labels or identifiers
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Library search"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Stack>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.25fr 0.95fr' },
            minHeight: 250,
            overflow: 'hidden',
          }}
        >
          {/* Left: search results */}
          <Box
            sx={{
              p: 1.6,
              borderRight: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.014),
              minWidth: 0,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                px: 1.15,
                py: 0.9,
                borderRadius: 2,
                bgcolor: 'background.paper',
                mb: 1,
              }}
            >
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Search sx={{ fontSize: 15, color: 'primary.main' }} />

                <Typography
                  variant="caption"
                  sx={{ fontWeight: 900, fontSize: 10.8 }}
                >
                  protein
                </Typography>

                <Box sx={{ flex: 1 }} />

                <FilterList sx={{ fontSize: 15, color: 'text.secondary' }} />
              </Stack>
            </Paper>

            <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {filters.map((filter) => (
                <Tag key={filter} label={filter} />
              ))}
            </Stack>

            <Stack spacing={0.7}>
              {ingredients.map((ingredient) => (
                <IngredientRow key={ingredient.name} ingredient={ingredient} />
              ))}
            </Stack>
          </Box>

          {/* Right: selected ingredient preview */}
          <Box sx={{ p: 1.6, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
              <Tune sx={{ fontSize: 16, color: 'primary.main' }} />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Selected ingredient
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                mb: 1,
              }}
            >
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Inventory2 sx={{ fontSize: 16 }} />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 900,
                        fontSize: 11.2,
                        lineHeight: 1.15,
                      }}
                    >
                      Whey protein concentrate
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: 9.8, mt: 0.2 }}
                    >
                      High-protein dairy powder for nutritional formulations.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                  {['Substance', 'Protein', 'Solid', 'Milk allergen'].map((tag) => (
                    <Tag
                      key={tag}
                      label={tag}
                      tone={tag.includes('allergen') ? 'warning' : 'default'}
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.85,
                mb: 1,
              }}
            >
              {previewValues.map((item) => (
                <Paper
                  key={item.label}
                  variant="outlined"
                  sx={{
                    p: 0.9,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      fontWeight: 800,
                      fontSize: 9.5,
                      lineHeight: 1.1,
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: 14.5,
                      lineHeight: 1.1,
                      fontWeight: 900,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Stack spacing={0.65}>
                {[
                  {
                    icon: <LocalDining sx={{ fontSize: 14 }} />,
                    label: 'Nutrition data available',
                  },
                  {
                    icon: <Science sx={{ fontSize: 14 }} />,
                    label: 'Technical attributes available',
                  },
                  {
                    icon: <CheckCircle sx={{ fontSize: 14 }} />,
                    label: 'Ready to reuse in formulations',
                  },
                ].map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    spacing={0.65}
                    alignItems="center"
                  >
                    <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
                      {item.icon}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 800, fontSize: 10.2 }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}